import { AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'
import {
    getAccessibleCompanies,
    switchCompany,
} from '@/lib/api/organization/organization-api'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
    setAccessibleCompanies,
    switchCompany as switchCompanyAction,
} from '@/store/slices/authSlice'
import { useEffect, useRef, useState } from 'react'
import { MdBusiness, MdCheck, MdSwapHoriz } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'

interface CompanySelectorProps {
    className?: string
}

export function CompanySelector({ className = '' }: CompanySelectorProps) {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { user, accessibleCompanies, isPartner, isMaster } = useAppSelector(
        state => state.auth
    )
    const [loading, setLoading] = useState(false)
    const [switching, setSwitching] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Buscar companies acessíveis ao montar
    useEffect(() => {
        const fetchCompanies = async () => {
            if (accessibleCompanies.length === 0 && !isMaster) {
                setLoading(true)
                try {
                    const response = await getAccessibleCompanies()
                    if (response.success && response.result) {
                        dispatch(setAccessibleCompanies(response.result))
                    }
                } catch (error) {
                    console.error('Erro ao buscar empresas acessíveis:', error)
                } finally {
                    setLoading(false)
                }
            }
        }
        fetchCompanies()
    }, [dispatch, accessibleCompanies.length])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const location = useLocation()

    // Para parceiros, a empresa atual pode não estar na lista de acessíveis
    // (pois o companyId do user aponta para a empresa dona do parceiro).
    // Nesse caso, usar a primeira empresa acessível do parceiro.
    const currentCompany =
        accessibleCompanies.find(c => c.id === user?.companyId) ||
        (isPartner ? accessibleCompanies[0] || null : null) ||
        (user?.company
            ? {
                  id: user.company.id,
                  name: user.company.name,
                  tradingName: user.company.tradingName,
              }
            : null)

    // Auto-switch: quando parceiro está logado na empresa principal (que não é dele),
    // trocar automaticamente para a primeira empresa acessível do parceiro
    const autoSwitchedRef = useRef(false)
    useEffect(() => {
        if (
            isPartner &&
            !autoSwitchedRef.current &&
            accessibleCompanies.length > 0 &&
            user?.companyId &&
            !accessibleCompanies.find(c => c.id === user.companyId)
        ) {
            autoSwitchedRef.current = true
            const targetCompany = accessibleCompanies[0]
            switchCompany(targetCompany.id)
                .then(response => {
                    if (response.success && response.result) {
                        dispatch(
                            switchCompanyAction({
                                token: response.result.access_token,
                                refreshToken: response.result.refresh_token,
                                company: response.result.company,
                                planModules: response.result.planModules,
                            })
                        )
                    }
                })
                .catch(() => {})
        }
    }, [isPartner, accessibleCompanies, user?.companyId, dispatch])

    const handleSwitchCompany = async (company: AccessibleCompany) => {
        if (company.id === currentCompany?.id || switching) return

        /**
         * Onde o operador está AGORA, lido antes de qualquer navegação.
         *
         * ⚠️ Era um `useState` inicializado na montagem do componente, e o
         * `setLastPath` logo antes do `navigate` não mudava o valor capturado pelo
         * `setTimeout` — o closure via o estado do render corrente. Como o seletor
         * mora no header e monta uma vez, a primeira troca sempre voltava para a
         * rota em que a sessão começou (a home), e as seguintes voltavam para a
         * rota da troca ANTERIOR. Uma variável local não tem esse problema.
         *
         * `search` e `hash` vão junto: a tela de onde se troca costuma ter filtro
         * e aba na URL, e voltar sem eles é perder o que estava em tela.
         */
        const currentPath = `${location.pathname}${location.search}${location.hash}`

        setSwitching(true)
        try {
            const response = await switchCompany(company.id)
            if (response.success && response.result) {
                dispatch(
                    switchCompanyAction({
                        token: response.result.access_token,
                        refreshToken: response.result.refresh_token,
                        company: response.result.company,
                        planModules: response.result.planModules,
                    })
                )
                setIsOpen(false)

                // A ida e volta pela home força as telas a remontar com o token e
                // a filial novos — sem ela, a página aberta continuaria exibindo os
                // dados da filial anterior. `replace` nos dois passos: senão o
                // "voltar" do navegador cairia na home intermediária.
                navigate('/', { replace: true })
                setTimeout(() => {
                    navigate(currentPath, { replace: true })
                }, 100)
            }
        } catch (error) {
            console.error('Erro ao trocar de empresa:', error)
        } finally {
            setSwitching(false)
        }
    }

    // Não mostrar se usuário tem acesso a apenas 1 company
    if ((accessibleCompanies.length <= 1 && !loading) || isMaster) {
        return null
    }

    return (
        /* `max-sm:static`: no celular o painel se posiciona contra o header
           (que é `relative`) e ocupa a largura da tela, em vez de sair pela
           lateral ancorado neste botão. */
        <div ref={dropdownRef} className={`relative max-sm:static ${className}`}>
            <button
                type="button"
                className="btn btn-ghost btn-sm gap-2 min-w-0 lg:min-w-[180px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <MdBusiness className="text-lg flex-shrink-0" />
                <span className="hidden lg:inline min-w-[100px] max-w-[140px] truncate text-left">
                    {currentCompany?.tradingName ||
                        currentCompany?.name ||
                        'Empresa'}
                </span>
                <MdSwapHoriz className="text-base opacity-60 flex-shrink-0" />
            </button>

            {isOpen && (
                <div
                    style={{
                        width: 'max-content',
                        minWidth: '260px',
                        maxWidth: 'min(520px, calc(100vw - 2rem))',
                    }}
                    className="absolute top-full left-0 mt-2 max-h-[60vh] overflow-y-auto bg-base-100 rounded-box shadow-xl border border-base-300 z-[9999] max-sm:!inset-x-2 max-sm:!w-auto max-sm:!min-w-0"
                >
                    <div className="px-4 py-3 border-b border-base-200 flex items-center justify-between">
                        <span className="text-sm font-semibold">
                            Mudar de Unidade
                        </span>
                        {switching && (
                            <span className="loading loading-spinner loading-xs" />
                        )}
                    </div>

                    <ul className="menu p-2">
                        {loading ? (
                            <li className="px-4 py-3 flex justify-center">
                                <span className="loading loading-spinner loading-sm" />
                            </li>
                        ) : (
                            accessibleCompanies.map(company => (
                                <li key={company.id}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSwitchCompany(company)
                                        }
                                        disabled={switching}
                                        className={`flex items-center gap-3 py-3 px-4 hover:bg-base-200 rounded-lg w-full text-left ${
                                            company.id === currentCompany?.id
                                                ? 'bg-secondary/10 border-l-2 border-secondary'
                                                : ''
                                        }`}
                                    >
                                        <div className="avatar placeholder">
                                            <div className="bg-base-300 text-base-content rounded-full w-8 h-8 flex items-center justify-center text-xs">
                                                {company.name
                                                    ?.charAt(0)
                                                    .toUpperCase() || 'E'}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium whitespace-nowrap leading-snug">
                                                {company.tradingName ||
                                                    company.name}
                                            </p>
                                            {company.tradingName &&
                                                company.name !==
                                                    company.tradingName && (
                                                    <p className="text-xs text-base-content/60 whitespace-nowrap leading-snug mt-0.5">
                                                        {company.name}
                                                    </p>
                                                )}
                                        </div>
                                        {company.id === currentCompany?.id && (
                                            <MdCheck className="text-secondary text-lg flex-shrink-0" />
                                        )}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    )
}
